import { readonly, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  loadStripe,
  Stripe,
  StripeCardElement,
  StripeCardNumberElement,
  StripeCardExpiryElement,
  StripeCardCvcElement,
  StripeElements,
  StripeElementLocale,
} from '@stripe/stripe-js';

import { addCardNew, confirmSetup, createSetupIntent, getSelfServicePaymentMethod } from '@/api/apiService';
import { useHandleErrorPayment } from '@/composables/domains/self-service/useHandleErrorPayment';
import { useToastStore } from '@/stores/toast';

const _elementCard = ref<StripeCardElement>();
const _elementCardNumber = ref<StripeCardNumberElement>();
const _elementCardExpiry = ref<StripeCardExpiryElement>();
const _elementCardCvc = ref<StripeCardCvcElement>();
const _elements = ref<StripeElements>();
const _isProcessingStripeAdd = ref(false);
const _stripe = ref<Stripe | null>(null);

export const useStripe = () => {
  const { t, locale } = useI18n();
  const { handleErrorAddCardStripe } = useHandleErrorPayment();
  const toastStore = useToastStore();
  let clientSecret = '';

  const isProcessingStripeAdd = readonly(_isProcessingStripeAdd);
  const elementCard = readonly(_elementCard);
  const elementCardNumber = readonly(_elementCardNumber);
  const elementCardExpiry = readonly(_elementCardExpiry);
  const elementCardCvc = readonly(_elementCardCvc);
  const elements = readonly(_elements);
  const stripe = readonly(_stripe);

  const setIsProcessingStripeAdd = (newVal: boolean) => {
    _isProcessingStripeAdd.value = newVal;
  };

  const setElementCard = (newVal: StripeCardElement | undefined) => {
    _elementCard.value = newVal;
  };

  const setElementCardNumber = (newVal: StripeCardNumberElement | undefined) => {
    _elementCardNumber.value = newVal;
  };

  const setElementCardExpiry = (newVal: StripeCardExpiryElement | undefined) => {
    _elementCardExpiry.value = newVal;
  };

  const setElementCardCvc = (newVal: StripeCardCvcElement | undefined) => {
    _elementCardCvc.value = newVal;
  };

  const setElements = (newVal: StripeElements | undefined) => {
    _elements.value = newVal;
  };

  // STRIPE elements 객체 생성
  const initStripeElements = async () => {
    const isProd = window.location.origin === 'https://sconchat.sconai.com';
    const clientKey = isProd
      ? 'pk_live_51SJUSh2IPaTzd9UPOBx3xVgTHGZNnej2HHVdE9HrY4nOIRCV1cAe90neT7w4wAVarJ2c1LR0VpAUay7fB4KL4TXl001Md6vz1S'
      : 'pk_test_51SJUSh2IPaTzd9UP5m20cx6MygpLXDspkGG4aH3YA58xjU36y66PQ71SJRW1MG3TMHJydwte8C3DhDmjBPfNG5L600lgOb4Phl';
    setElements(undefined);
    setElementCard(undefined);
    if (!_stripe.value) {
      _stripe.value = await loadStripe(clientKey);
    }
    setElements(stripe.value?.elements({ locale: locale.value as StripeElementLocale }));
  };

  // 카드 등록 UI 생성
  const createDomCard = async () => {
    if (_elementCard.value) return;

    setElementCard(
      _elements.value?.create('card', {
        disableLink: true, // TODO 삭제 (자동입력 기능 활성화)
        hidePostalCode: true,
      })
    );
  };

  // 카드 등록 UI 생성
  const createDomCardNumber = async () => {
    if (_elementCardNumber.value) return;

    setElementCardNumber(_elements.value?.create('cardNumber', { disableLink: true }));
  };

  // 카드 만료 연월 UI 생성
  const createDomCardExpiry = async () => {
    if (_elementCardExpiry.value) return;

    setElementCardExpiry(_elements.value?.create('cardExpiry'));
  };

  // Cvc UI 생성
  const createDomCardCvc = async () => {
    if (_elementCardCvc.value) return;

    setElementCardCvc(_elements.value?.create('cardCvc'));
  };

  // 카드 추가
  const handleCardAdd = async () => {
    const { data } = await createSetupIntent();
    clientSecret = data;
    const resConfirmSetup = await stripe.value?.confirmCardSetup(clientSecret, {
      payment_method: {
        card: _elementCardNumber.value!,
      },
    });
    if (!resConfirmSetup?.setupIntent) {
      handleErrorAddCardStripe(resConfirmSetup?.error.code || '');
      return false;
    }
    const paramsConfirmSetup = {
      setupIntentId: resConfirmSetup.setupIntent.id,
    };
    const { success } = await confirmSetup(paramsConfirmSetup);
    if (success) {
      await addCardNew('STRIPE');
      toastStore.info(t('ss_card_add_success'));
    } else {
      toastStore.error(t('ss_card_add_fail'));
    }
    return success;
  };

  // 카드 정보 조회
  const getSetupIntent = async () => {
    const resSetupIntent = await stripe.value?.retrieveSetupIntent(clientSecret);
    console.debug('resSetupIntent', resSetupIntent);
  };

  // 결제수단 조회
  const getPaymentMethod = async () => {
    await getSelfServicePaymentMethod('STRIPE');
  };

  return {
    isProcessingStripeAdd,
    elementCard,
    elementCardNumber,
    elementCardExpiry,
    elementCardCvc,
    elements,
    createDomCard,
    createDomCardNumber,
    createDomCardExpiry,
    createDomCardCvc,
    initStripeElements,
    handleCardAdd,
    getSetupIntent,
    getPaymentMethod,
    setIsProcessingStripeAdd,
    setElementCard,
    setElements,
  };
};
